/**
 * ═══════════════════════════════════════════════════════════════════
 *  TourOverlay — Composes Spotlight + Tooltip for the current step
 * ═══════════════════════════════════════════════════════════════════
 *
 *  Manages:
 *  - Finding the target element for the current step
 *  - Updating target rect on scroll/resize
 *  - Rendering Spotlight + Tooltip in a portal
 *  - Calling onEnter/onExit step callbacks
 *  - Keyboard navigation (Escape to skip, Arrow keys)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import TourSpotlight from './TourSpotlight';
import TourTooltip from './TourTooltip';
import { findTargetElement, getElementRect, scrollToElement } from './utils';
import { TOUR_STATUS } from './tourReducer';

export default function TourOverlay({
  status,
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  overlayColor,
  isDark = false,
}) {
  const [targetRect, setTargetRect] = useState(null);
  const [targetEl, setTargetEl] = useState(null);
  const prevStepRef = useRef(null);
  const rafRef = useRef(null);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  // ── Find target & calculate rect ──────────────────────────────────
  const updateRect = useCallback(() => {
    if (!targetEl) {
      setTargetRect(null);
      return;
    }
    const rect = getElementRect(targetEl, step?.spotlightPadding ?? 8);
    setTargetRect(rect);
  }, [targetEl, step]);

  // Find element when step changes
  useEffect(() => {
    if (status !== TOUR_STATUS.RUNNING || !step) {
      setTargetEl(null);
      setTargetRect(null);
      return;
    }

    let cancelled = false;

    // Call onExit for previous step
    if (prevStepRef.current && prevStepRef.current.id !== step.id && prevStepRef.current.onExit) {
      try { prevStepRef.current.onExit(); } catch (e) { console.warn('[Tour] onExit error:', e); }
    }
    prevStepRef.current = step;

    // No target selector → centered modal (welcome/completion steps)
    if (!step.target) {
      setTargetEl(null);
      setTargetRect(null);
      if (step.onEnter) {
        try { step.onEnter(); } catch (e) { console.warn('[Tour] onEnter error:', e); }
      }
      return;
    }

    // Find the target element (with retries for async-rendered elements)
    const retries = step.waitForSelector ? 10 : 3;
    findTargetElement(step.target, retries, 250).then((el) => {
      if (cancelled) return;
      if (el) {
        setTargetEl(el);
        scrollToElement(el);
        if (step.onEnter) {
          try { step.onEnter(el); } catch (e) { console.warn('[Tour] onEnter error:', e); }
        }
      } else {
        // Target not found — auto-advance to next step
        console.warn(`[Tour] Target "${step.target}" not found — auto-advancing`);
        setTargetEl(null);
        setTargetRect(null);
        setTimeout(() => {
          if (!cancelled) onNextRef.current();
        }, 150);
      }
    });

    return () => { cancelled = true; };
  }, [step, stepIndex, status]); // stepIndex ensures re-run on back navigation

  // Update rect when target element changes
  useEffect(() => {
    updateRect();
  }, [targetEl, updateRect]);

  // ── Live updates (scroll, resize, mutations) ──────────────────────
  useEffect(() => {
    if (status !== TOUR_STATUS.RUNNING) return;

    const tick = () => {
      updateRect();
      rafRef.current = requestAnimationFrame(tick);
    };

    // Use rAF loop for smooth tracking
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [status, updateRect]);

  // ── Keyboard — only Escape (let user type freely in inputs) ───────
  useEffect(() => {
    if (status !== TOUR_STATUS.RUNNING) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [status, onSkip]);

  // ── Don't render if not running ───────────────────────────────────
  if (status !== TOUR_STATUS.RUNNING || !step) return null;

  return createPortal(
    <>
      <TourSpotlight
        targetRect={targetRect}
        overlayColor={overlayColor}
        onClick={(e) => {
          // Clicking overlay outside tooltip — do nothing (don't skip accidentally)
          e.stopPropagation();
        }}
      />
      <TourTooltip
        key={`tour-step-${stepIndex}`}
        step={step}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        targetRect={targetRect}
        onNext={onNext}
        onPrev={onPrev}
        onSkip={onSkip}
        isDark={isDark}
      />
    </>,
    document.body
  );
}
